import React from 'react';
import type { MusicVideoEffect } from 'fym-shared/src/music24/musicVideoEffects';
import { beatPulse, effectEnvelope, effectProgress } from 'fym-shared/src/music24/musicVideoEffectFrames';
import { TypedMemo } from '../../../common/types';
import { beatIndexAt, effectColour, framePx, intensityOf, textParam } from '../previewEffectsMath';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for these parts of the editor; each part uses its own slice of it */
import styles from './PreviewEffectSketches.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ShotSketchProps {
  effect: MusicVideoEffect;
  sec: number;
  accent: string;
  beats: readonly number[];
}

/** The panels of a split screen, as the render: two, three or four. */
const PANEL_DIVIDERS: Record<'halves' | 'thirds' | 'grid', ReadonlyArray<{ vertical: boolean; at: number }>> = {
  halves: [{ vertical: true, at: 50 }],
  thirds: [
    { vertical: true, at: 33.333 },
    { vertical: true, at: 66.667 },
  ],
  grid: [
    { vertical: true, at: 50 },
    { vertical: false, at: 50 },
  ],
};

/**
 * The part of a shot effect over the footage (the sketch of `ShotEffects.tsx` of the render). Split
 * screen: the panel edges come in on the beats (the preview does not copy the footage into the
 * panels). Freeze and outline: the stroke draws round the frame and a flash marks the hit (the
 * preview does not freeze the frame). Colour pulse: the tint on every beat. The shake and the colour
 * split move the footage itself (`previewFootageMotion`).
 */
const ShotSketchComponent: React.FC<ShotSketchProps> = ({ effect, sec, accent, beats }) => {
  const progress = effectProgress(effect, sec);
  if (progress === null) {
    return null;
  }
  const intensity = intensityOf(effect);
  const envelope = effectEnvelope(effect, sec);
  switch (effect.kind) {
    case 'splitScreen': {
      const layout = textParam(effect, 'layout', 'halves');
      const dividers = PANEL_DIVIDERS[layout === 'thirds' || layout === 'grid' ? layout : 'halves'];
      const firstBeat = beatIndexAt(effect.startSec, beats);
      const shown = Math.max(1, beatIndexAt(sec, beats) - firstBeat + 1);
      return (
        <div className={styles.fill} style={{ opacity: envelope }}>
          {dividers.slice(0, shown).map((divider) => {
            const size = `${framePx(6 + 6 * intensity)}cqw`;
            return (
              <span
                key={`${divider.vertical ? 'x' : 'y'}-${divider.at}`}
                className={divider.vertical ? styles.panelEdgeX : styles.panelEdgeY}
                style={
                  divider.vertical
                    ? { left: `${divider.at}%`, width: size, background: accent }
                    : { top: `${divider.at}%`, height: size, background: accent }
                }
              />
            );
          })}
        </div>
      );
    }
    case 'freezeStroke': {
      const colour = effectColour(effect, accent, 'white');
      const draw = Math.min(1, progress * 2.2);
      return (
        <>
          <div
            className={styles.fill}
            style={{ background: '#fff', opacity: Math.max(0, 1 - progress * 6) * (0.4 + 0.5 * intensity) }}
          />
          <div
            className={styles.stroke}
            style={{
              borderColor: colour,
              borderWidth: `${framePx(6 + 8 * intensity)}cqw`,
              clipPath: `inset(0 ${(1 - draw) * 100}% 0 0)`,
              opacity: envelope,
            }}
          />
        </>
      );
    }
    case 'colourPulse': {
      const pulse = beatPulse(sec, beats);
      return (
        <div
          className={styles.tint}
          style={{
            background: `radial-gradient(70% 70% at 50% 50%, ${effectColour(effect, accent, 'accent')}, transparent 75%)`,
            opacity: (0.12 + 0.45 * pulse) * intensity * envelope,
          }}
        />
      );
    }
    case 'lyricKinetic':
    case 'lyricHook':
    case 'titleCard':
    case 'artistCard':
    case 'chapterCard':
    case 'whip':
    case 'flashCut':
    case 'filmBurn':
    case 'glitch':
    case 'zoomThrough':
    case 'rgbSplit':
    case 'beatShake':
    default:
      return null;
  }
};

export const ShotSketch = TypedMemo(ShotSketchComponent);
